import { RestSchemas } from '@/contexts/QueryProvider'
import { isWorkspaceDirectory } from '../WorkspaceFilesPage/WorkspaceFilesTable/workspaceFilesTableUtils'

export type FileItem = RestSchemas['Messages.Storage.StorageDetailItemWorkspace']

const ARQ_PLACEHOLDER_SUFFIX = '/.arq_placeholder'

export type WorkspaceFileTreeFileNode = FileItem & { nodeKind: 'file' }

export type WorkspaceFileTreeFolderNode = {
  nodeKind: 'folder'
  name: string
  /** Prefix path for this folder (no trailing slash), e.g. `showcase/chats/162` */
  path: string
  children: WorkspaceFileTreeNode[]
  /** Present when the API returned an explicit row for this folder */
  item?: FileItem
}

export type WorkspaceFileTreeNode = WorkspaceFileTreeFileNode | WorkspaceFileTreeFolderNode

function isDirectoryListingItem(item: FileItem): boolean {
  return item.properties?.is_directory === 'true' || isWorkspaceDirectory(item)
}

export function transformFilesToTree(files: FileItem[]): WorkspaceFileTreeNode[] {
  const filtered = files.filter((f) => !f.blobName.endsWith(ARQ_PLACEHOLDER_SUFFIX))

  type MutableFolder = WorkspaceFileTreeFolderNode

  const root: MutableFolder = {
    nodeKind: 'folder',
    name: '',
    path: '',
    children: [],
  }

  function findChildFolder(parent: MutableFolder, segment: string): MutableFolder | undefined {
    for (const child of parent.children) {
      if (child.nodeKind === 'folder' && child.name === segment) {
        return child
      }
    }
    return undefined
  }

  function getOrCreateFolder(parent: MutableFolder, segment: string): MutableFolder {
    const existing = findChildFolder(parent, segment)
    if (existing) {
      return existing
    }
    const path = parent.path ? `${parent.path}/${segment}` : segment
    const folder: MutableFolder = {
      nodeKind: 'folder',
      name: segment,
      path,
      children: [],
    }
    parent.children.push(folder)
    return folder
  }

  function insertItem(item: FileItem) {
    const segments = item.blobName.split('/').filter(Boolean)
    if (segments.length === 0) {
      return
    }

    const asDirectory = isDirectoryListingItem(item)
    const folderSegments = asDirectory ? segments : segments.slice(0, -1)

    let parent = root
    for (const segment of folderSegments) {
      parent = getOrCreateFolder(parent, segment)
    }

    if (asDirectory) {
      parent.item = item
      return
    }

    parent.children.push({ ...item, nodeKind: 'file' })
  }

  for (const item of filtered) {
    insertItem(item)
  }

  function compareNodes(a: WorkspaceFileTreeNode, b: WorkspaceFileTreeNode): number {
    if (a.nodeKind !== b.nodeKind) {
      return a.nodeKind === 'folder' ? -1 : 1
    }
    const nameA = a.nodeKind === 'folder' ? a.name : a.filename
    const nameB = b.nodeKind === 'folder' ? b.name : b.filename
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' })
  }

  function sortTree(folder: MutableFolder) {
    folder.children.sort(compareNodes)
    for (const child of folder.children) {
      if (child.nodeKind === 'folder') {
        sortTree(child)
      }
    }
  }

  sortTree(root)
  return root.children
}
